import { defineStore } from "pinia";

import { TransactionState, Hit, Filters } from "../interfaces/knowledge-hub";
import { Filter, SingleFilter } from "../interfaces/facets";
import { QueryParams } from "../interfaces/query-parameters";

import { useSearchParameters, useTypesenseClient } from "../composables/typesense";
import { useQueryArrayUrlify, useRemoveQueryUrlify, useQueryUrlify } from "../composables/query-parameters";

export const useTransactionStore = defineStore("transactions", {
	state: (): TransactionState => ({
		loading: false,
		facets: [],
		filters: {
			searchQuery: "",
			filterQuery: "",
			date: {
				field: "postDateYear",
				values: [],
				combination: "&&",
			},
		},
		pagination: {
			current: 0,
			totalPages: null,
			totalResults: null,
			foundResults: null,
			itemsPerPage: 24,
		},
		sorting: {
			type: "post_date_timestamp",
			order: "desc",
		},
		index: `wdp_${window.typesense_environment}_transactions`,
		transactions: [],
	}),
	actions: {
		async fetch(reset = false, queryParams?: Array<QueryParams>) {
			this.loading = true;

			// set URL query parameters if existing
			if (queryParams) {
				this.setQueryFilters(queryParams);
			}

			this.setFiltersQuery();

			const client = useTypesenseClient();
			const searchParameters = useSearchParameters(this.filters, this.sorting);

			if (!reset && this.pagination.current) {
				this.pagination.current += 1;
			} else {
				this.pagination.current = 1;
				this.pagination.totalPages = 0;
				this.pagination.totalResults = null;
				this.transactions = [];
			}

			const baseFilter = `site:=${window.language} && hideInFrontEndSearch:=false`;

			const searchRequests = {
				searches: [
					{
						highlight_full_fields: "meta,slug",
						collection: this.index,
						facet_by: "postDateYear",
						page: this.pagination.current,
						per_page: this.pagination.itemsPerPage,
						...searchParameters,
					},
					{
						collection: this.index,
						q: "*",
						max_facet_values: 100,
						facet_by: "postDateYear",
						page: 1,
						per_page: 0,
						filter_by: baseFilter,
					},
				],
			};

			await client.multiSearch
				.perform(searchRequests)
				.then((results: any) => {
					const filteredResults = results.results[0];

					this.facets = results.results[1].facet_counts;
					this.facetsAfterFilter = results.results[0].facet_counts;
					this.pagination.current = filteredResults.page;
					this.pagination.totalPages = this.pagination.itemsPerPage
						? Math.ceil(filteredResults.found / this.pagination.itemsPerPage)
						: 0;
					this.pagination.foundResults = filteredResults.found;
					this.pagination.totalResults = filteredResults.out_of;

					if (filteredResults.hits) {
						const hits: Array<Hit> = filteredResults.hits;

						// set results to the projects if it's a reset otherwise add the filteredResults to the existing ones
						if (reset) {
							this.transactions = hits;
						} else {
							this.transactions = [...(this.transactions || []), ...hits];
						}
					}
				})
				.catch((error) => {
					console.error("Typesense search failed:", error);
				})
				.finally(() => {
					this.loading = false;
				});
		},
		setFilter(type: keyof Filters, value: string) {
			const settedFilter: Filter | SingleFilter | string = this.filters[type];

			if (typeof settedFilter !== "string") {
				if (settedFilter.hasOwnProperty("values")) {
					if (settedFilter.values.indexOf(value) < 0) {
						settedFilter.values.push(value);
						useQueryArrayUrlify(type, value);
					} else {
						settedFilter.values = settedFilter.values.filter((item: string) => item !== value);
						useRemoveQueryUrlify(type, value);
					}
				}

				if (settedFilter.hasOwnProperty("value")) {
					settedFilter.value = value;

					// useRemoveQueryUrlify(type)

					// if (value !== null && value !== '' && value !== false) {
					//     useQueryArrayUrlify(type, value)
					// }
					useQueryUrlify(type, value);
				}
			}

			this.fetch(true);
		},
		setFiltersQuery() {
			const filterParts: string[] = [];

			// Always add the base filters
			filterParts.push(`site:=${window.language}`);
			filterParts.push(`hideInFrontEndSearch:=false`);

			if (this.filters.date.values.length > 0) {
				filterParts.push(`${this.filters.date.field}:=[${this.filters.date.values.join(",")}]`);
			}

			this.filters.filterQuery = filterParts.join(" && ");
		},
		setQueryFilters(queryParams: Array<QueryParams>) {
			for (const key in queryParams as QueryParams[]) {
				const values = queryParams[key].values;
				const type: string = queryParams[key].type;

				if (values) {
					const filter = this.filters[type as keyof Filters];

					if (filter.hasOwnProperty("values")) {
						(filter as Filter).values = values.map((item: { name: string }) => item.name);
					}

					if (filter.hasOwnProperty("value")) {
						const singleValue = values.map((item: { name: string }) => item.name)[0];
						if (typeof (filter as SingleFilter).value === "boolean") {
							(filter as SingleFilter).value = singleValue === "true";
						} else {
							(filter as SingleFilter).value = singleValue;
						}
					}
				}
			}
		},
	},
});
